import { useEffect } from "react";
import { ArrowLeft, ShieldCheck, Scale } from "lucide-react";

const PRIVACY_SECTIONS = [
  ["Information collected", "The website records a visitor's IP address, browser user-agent and visit timestamp for aggregate visitor and download statistics. Approximate city and country information is collected only when optional geolocation is explicitly enabled by the operator. The website does not ask public visitors to create an account."],
  ["How information is used", "This limited information is used to operate the website, measure genuine usage, protect the administrator login from repeated failed attempts and maintain download records. It is not used for advertising or sold to third parties."],
  ["Local browser storage", "The website stores the selected light or dark theme in local storage and a session-only marker to avoid counting the same visit repeatedly during one browser session. No advertising or cross-site tracking cookies are used."],
  ["Storage and access", "Operational records are stored in the website database. Access is restricted to the authenticated YKD ENTERPRISES administrator. Records can be exported for administration and removed using the protected log-clearing function."],
  ["External services", "The production service uses infrastructure providers for website hosting and database storage. Approximate visitor city and country are looked up through ipwho.is using the visitor's public IP address; exact GPS location is not collected. An installer download may later redirect to an official YIIS-100 release hosted by GitHub."],
  ["Your choices", "You may browse without downloading the product. Browser storage can be removed using your browser controls. Requests concerning website records may be submitted to YKD ENTERPRISES through its published business support channel."],
  ["Updates", "This notice may be updated when website functions, service providers or legal requirements change. The effective date shown on this page identifies the current version."],
];

const TERMS_SECTIONS = [
  ["Website purpose", "This website provides information about YIIS-100, an IPO intelligence and record-management application developed by YKD ENTERPRISES. Website content may be updated as the product evolves."],
  ["No financial advice", "YIIS-100 and this website provide software tools and informational material only. Nothing on the website is investment, legal, tax or financial advice, an offer, or a recommendation to apply for or trade any security."],
  ["Product availability", "The installer is currently unavailable. Disabled download controls do not represent an error. A download will be enabled only after an official installer has been prepared and published."],
  ["User responsibility", "Users remain responsible for checking their entries, maintaining backups, protecting local credentials and independently verifying market, allotment, listing and financial information before relying on it."],
  ["Intellectual property", "The YIIS-100 name, website design, product materials and application content belong to YKD ENTERPRISES or their respective licensors. They may not be copied, republished or misrepresented as another product without permission."],
  ["Availability and liability", "The website and product information are provided on an as-available basis. To the extent permitted by applicable law, YKD ENTERPRISES is not responsible for losses caused by interrupted access, inaccurate third-party information, user input errors or decisions made using the software."],
  ["Acceptable use", "Do not attempt unauthorized access, interfere with the service, bypass security controls, submit malicious traffic or use the website in violation of applicable law."],
  ["Changes", "These terms may change with the website or product. Continued use after an updated effective date means the current terms apply to subsequent use."],
];

const CONTENT = {
  privacy: {
    eyebrow: "Privacy notice",
    title: "PRIVACY POLICY",
    intro: "A plain-language explanation of the limited information processed by the YIIS-100 website.",
    icon: ShieldCheck,
    sections: PRIVACY_SECTIONS,
  },
  terms: {
    eyebrow: "Website terms",
    title: "TERMS OF USE",
    intro: "The conditions that apply when accessing the YIIS-100 website and product information.",
    icon: Scale,
    sections: TERMS_SECTIONS,
  },
};

export default function Legal({ type }) {
  const content = CONTENT[type];
  const Icon = content.icon;

  useEffect(() => {
    document.documentElement.classList.toggle("dark", (localStorage.getItem("yiis-theme") || "dark") === "dark");
    document.title = `${content.title} | YIIS-100`;
    window.scrollTo(0, 0);
    return () => {
      document.title = "YIIS-100 — IPO Intelligence Edition | YKD ENTERPRISES";
    };
  }, [content]);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid={`${type}-page`}>
      <header className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur-xl">
        <div className="max-w-[1000px] mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground hover:text-accent transition-colors">
            <ArrowLeft size={14} /> Back to site
          </a>
          <span className="flex items-center gap-2.5">
            <img src="/logo.png" alt="YIIS-100 logo" className="w-7 h-7 rounded-md" />
            <span className="hidden sm:inline font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground">YKD Enterprises</span>
          </span>
        </div>
      </header>

      <main className="max-w-[1000px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="flex items-center gap-3 mb-6 text-accent">
          <Icon size={20} />
          <span className="font-mono text-xs tracking-[0.25em] uppercase">{content.eyebrow}</span>
        </div>
        <h1 className="font-display font-semibold tracking-tight text-4xl sm:text-5xl md:text-6xl mb-6">{content.title}</h1>
        <p className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-2xl mb-5">{content.intro}</p>
        <p className="font-mono text-[11px] tracking-[0.16em] uppercase text-muted-foreground mb-16">Effective 2 September 2026</p>

        <div className="border-t border-border">
          {content.sections.map(([heading, body], index) => (
            <section key={heading} className="grid md:grid-cols-12 gap-4 md:gap-10 py-9 border-b border-border">
              <div className="md:col-span-1 font-mono text-xs text-accent tabular-nums">{String(index + 1).padStart(2, "0")}</div>
              <h2 className="md:col-span-4 font-display text-xl font-semibold tracking-tight">{heading}</h2>
              <p className="md:col-span-7 text-sm md:text-base leading-7 text-muted-foreground">{body}</p>
            </section>
          ))}
        </div>

        <div className="mt-12 flex flex-wrap gap-6 font-mono text-xs tracking-[0.15em] uppercase">
          <a href="/privacy" className="hover:text-accent transition-colors">Privacy</a>
          <a href="/terms" className="hover:text-accent transition-colors">Terms</a>
          <a href="/help" className="hover:text-accent transition-colors">User guide</a>
        </div>
      </main>
    </div>
  );
}
